import * as z from 'zod';

import {
    DISPLAY_SETTING_ACTIONS,
    DISPLAY_SETTING_MAX_DURATION_SECONDS,
} from 'src/features/systems/utils/displaySettingConstants';
import { SYSTEM_SETTINGS_CATEGORY } from 'src/features/systems/utils/types';

export type DisplaySettingValueSchemaValues = z.infer<typeof displaySettingValue>;
export type DisplaySettingCreateFormSchemaValues = z.infer<typeof displaySettingCreateFormSchema>;
export type DisplaySettingFormSchemaValues = z.infer<typeof displaySettingFormSchema>;

// `nullish` rather than `optional` throughout: `Setting.value` is free-form JSON on the backend and
// the display-setting endpoint stores explicit nulls for fields the caller left out, so a record
// written outside this form can arrive with `"title_color": null`. Rejecting that here only makes
// the whole form silently unsubmittable - the values are dropped or defaulted downstream anyway.
const displaySettingValue = z.object({
    title: z.string().nullish(),
    subtitle: z.string().nullish(),
    title_color: z.string().nullish(),
    subtitle_color: z.string().nullish(),
    // 0 is allowed on purpose: the number input collapses a cleared field to 0, which is submitted
    // as "no duration configured" (the key is dropped) so the backend falls back to its default.
    duration_seconds: z.number().int().min(0).max(DISPLAY_SETTING_MAX_DURATION_SECONDS).nullish(),
});

export const displaySettingCreateFormSchema = z.object({
    category: z.enum(SYSTEM_SETTINGS_CATEGORY),
    code: z.string(),
    value: displaySettingValue,
});

const displaySettingFormSchemaShape = Object.fromEntries(
    DISPLAY_SETTING_ACTIONS.map((action) => [action, displaySettingValue]),
) as Record<(typeof DISPLAY_SETTING_ACTIONS)[number], typeof displaySettingValue>;

export const displaySettingFormSchema = z.object(displaySettingFormSchemaShape);
